import { Box, Popper, Button, useTheme, Typography, IconButton, Grow, alpha } from "@mui/material";
import { useCallback, useEffect, useMemo, useState, useLayoutEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useContextProvider } from "../page-context/ContextProvider";
import { MdRefresh, MdOutlineStarBorder, MdClose } from "react-icons/md";
import { StandardSwitch } from "../switch/StandardToggle";
import { getVisibleFieldsForView } from "../grid/columnDefinitions/envFactorViewModes";

// Module-level persistent cache for scroll positions per colVisibility_key
const scrollPositionCache = {};

const ColumnMenu = ({ anchorRef, visible, onClose, colDefs, colVisibility_key, defaultCols, gridRef, activeView }) => {
  const { columnVisibilities, setColumnVisibilities } = useContextProvider();
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  
  const [colVisibility, setColVisibility] = useState(JSON.parse(localStorage.getItem(colVisibility_key)) || defaultCols);
  const scrollContainerRef = useRef(null);

  // Preserve scroll position across renders & unmounts
  useLayoutEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = scrollPositionCache[colVisibility_key] || 0;
    }
  }); // Run on every render to ensure it stays in place

  const handleScroll = (e) => {
    scrollPositionCache[colVisibility_key] = e.target.scrollTop;
  };

  const toggleVisibility = useCallback(
    (field) => {
      setColVisibility((prev) => {
        // Map of main fields to their corresponding unit fields
        const unitMapping = {
          "length": "length_unit",
          "width": "width_unit",
          "thickness": "thickness_unit",
          "user_length": "user_length_unit",
          "user_width": "user_width_unit",
          "user_thickness": "user_thickness_unit",
          "user_volume": "user_volume_unit"
        };
        const unitField = unitMapping[field];
        
        let next;
        if (prev.includes(field)) {
          next = prev.filter((item) => item !== field && item !== unitField);
        } else {
          next = [...prev, field];
          if (unitField && !next.includes(unitField)) {
            next.push(unitField);
          }
        }
        return next;
      });
    },
    []
  );
  
  useEffect(() => {
    setColumnVisibilities((prev) => {
      if (JSON.stringify(prev[colVisibility_key]) === JSON.stringify(colVisibility)) return prev;
      return { ...prev, [colVisibility_key]: colVisibility };
    });
  }, [colVisibility, colVisibility_key, setColumnVisibilities]);

  const handleSetDefault = () => {
    localStorage.setItem(colVisibility_key, JSON.stringify(colVisibility));
    if (gridRef && gridRef.current) {
      const columnState = gridRef.current.api.getColumnState();
      const columnWidths = {};
      columnState.forEach((col) => {
        if (col.colId && col.width) {
          columnWidths[col.colId] = { width: col.width, flex: col.flex };
        }
      });
      localStorage.setItem(`${colVisibility_key}_widths`, JSON.stringify(columnWidths));
    }
    alert(t("Saved as default"));
  };

  const handleResetDefault = () => {
    localStorage.removeItem(colVisibility_key);
    setColVisibility(defaultCols);
    localStorage.removeItem(`${colVisibility_key}_widths`);
    if (gridRef && gridRef.current) {
      gridRef.current.api.resetColumnState();
    }
  };

  const filteredCols = useMemo(() => {
    let cols = colDefs.filter((c) => c.field !== "id");
    
    // Filter by active view
    const visibleFields = getVisibleFieldsForView(activeView);
    if (visibleFields) {
      cols = cols.filter((c) => visibleFields.includes(c.field));
    }
    
    // Hide unit columns from being listed as separate checkboxes
    const unitFieldsToHide = [
      "length_unit", "width_unit", "thickness_unit",
      "user_length_unit", "user_width_unit", "user_thickness_unit", "user_volume_unit"
    ];
    cols = cols.filter((c) => !unitFieldsToHide.includes(c.field));
    
    return cols;
  }, [colDefs, activeView]);

  const visibleCount = filteredCols.filter((c) => colVisibility.includes(c.field)).length;
  
  const primaryColor = isDark ? "#4ade80" : "#659d33";

  return (
    <Popper
      elevation={0}
      anchorEl={anchorRef.current}
      placement="bottom-end"
      open={visible}
      style={{ zIndex: 1300 }}
      disablePortal
      modifiers={[
        {
          name: "preventOverflow",
          enabled: true,
          options: {
            boundary: "viewport",
            altAxis: true,
            padding: 12,
          },
        },
        {
          name: "flip",
          enabled: true,
          options: {
            boundary: "viewport",
            padding: 12,
          },
        },
      ]}
    >
      <Box
        sx={{
          backgroundColor: theme.palette.background.paper,
          border: 1,
          borderColor: theme.palette.divider,
          borderRadius: 3,
          boxShadow: isDark ? "0px 10px 40px rgba(0, 0, 0, 0.5)" : "0px 10px 40px rgba(0, 0, 0, 0.1)",
          overflow: "hidden",
          maxWidth: "min(100vw - 20px, 900px)",
          mt: 1,
          fontFamily: "Poppins, sans-serif"
        }}>
        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, py: 1.5, backgroundColor: isDark ? "rgba(255,255,255,0.02)" : "#fafafa", borderBottom: 1, borderColor: theme.palette.divider }}>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button size="small" variant="text" sx={{ color: primaryColor, textTransform: "none", fontWeight: 500, borderRadius: 2, px: 1.5, "&:hover": { backgroundColor: `${primaryColor}15` } }} onClick={handleResetDefault} startIcon={<MdRefresh size={18} />}>
              {t("Reset")}
            </Button>
            <Button size="small" variant="text" sx={{ color: primaryColor, textTransform: "none", fontWeight: 500, borderRadius: 2, px: 1.5, "&:hover": { backgroundColor: `${primaryColor}15` } }} onClick={handleSetDefault} startIcon={<MdOutlineStarBorder size={18} />}>
              {t("Set Default")}
            </Button>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography sx={{ fontSize: "13px", fontWeight: 500, color: theme.palette.text.secondary }}>
              {visibleCount}/{filteredCols.length} visible
            </Typography>
            <IconButton size="small" onClick={onClose} sx={{ color: theme.palette.text.secondary, "&:hover": { color: theme.palette.error.main, backgroundColor: `${theme.palette.error.main}15` } }}>
              <MdClose size={18} />
            </IconButton>
          </Box>
        </Box>

        {/* Progress Bar */}
        <Box sx={{ height: 2, width: "100%", backgroundColor: theme.palette.divider }}>
          <Box sx={{ height: "100%", backgroundColor: primaryColor, width: `${(visibleCount / filteredCols.length) * 100}%`, transition: "width 0.4s ease-out" }} />
        </Box>

        <Box
          ref={scrollContainerRef}
          onScroll={handleScroll}
          sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", columnGap: 1.5, rowGap: 1, p: 2, maxHeight: "320px", overflowY: "auto", overflowX: "hidden" }}
        >
          {filteredCols.map((c) => {
            const isVisible = colVisibility.includes(c.field);
            return (
              <Box
                key={c.field}
                onClick={() => toggleVisibility(c.field)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  px: 1.5,
                  py: 0.85,
                  borderRadius: "8px",
                  cursor: "pointer",
                  border: "1px solid",
                  borderColor: isVisible ? alpha(primaryColor, isDark ? 0.35 : 0.25) : (isDark ? "rgba(255,255,255,0.06)" : "#f1f5f9"),
                  bgcolor: isVisible ? alpha(primaryColor, isDark ? 0.08 : 0.05) : "transparent",
                  transition: "all 0.15s ease",
                  "&:hover": {
                    borderColor: primaryColor,
                    bgcolor: alpha(primaryColor, isDark ? 0.12 : 0.08),
                  },
                }}
              >
                <Typography sx={{ fontSize: "13px", fontWeight: 500, color: theme.palette.text.primary, pr: 1.5, userSelect: "none" }}>
                  {c.headerName || c.field}
                </Typography>
                <StandardSwitch checked={isVisible} onChange={() => toggleVisibility(c.field)} activeColor={primaryColor} isDark={isDark} />
              </Box>
            );
          })}
        </Box>
        
      </Box>
    </Popper>
  );
};

export default ColumnMenu;
